import React from 'react';
import PropTypes from 'prop-types';
import {
  CASCO_QUOTATION_DATA_CHANGED,
  CONVENTIONS_QUOTATIONS_COMPUTING,
  CONVENTIONS_QUOTATIONS_RETRIEVED,
  INSURANCE_QUOTATIONS_ARCHIVED
} from '../subscriptions';
import { useLocalSubscription } from 'hooks';
import { useApolloClient } from '@apollo/client';
import { quotationsInitializedVar } from 'features/insuranceDocument/variables';
import { useDocumentId, useOverRouterContextUtils, useQueryWithSimpleErrorHandling, useToast } from 'hooks';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { insuranceQuotationsStore } from './QuotationsListContainer';
import { INSURANCE_DOCUMENT_UPDATED } from 'features/insuranceDocument/subscriptions/insuranceDocumentSubscriptions';
import { INSURANCE_DATA_QUERY } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { hasHistoryVar, headerDataVar } from '../variables';
import { HAS_CASCO_QUOTATIONS_HISTORY_QUERY } from 'features/insuranceDocument/edit/general/cascoOffersHistory/queries';
import { useTranslation } from 'react-i18next';
import { updateCachedQuotations } from '../quotationsUtils';

const QuotationsSubscriptionsArea = ({ contextRef, setInternalValues }) => {
  const apolloClient = useApolloClient();
  const [t] = useTranslation();
  const addToast = useToast();

  const { setEvents } = useOverRouterContextUtils(contextRef);

  const { documentId } = useDocumentId();
  const {
    store: { refetch }
  } = useListBrowserUtils(insuranceQuotationsStore);

  setEvents({ onCascoQuotationsDataChanged: () => refetch() });

  useQueryWithSimpleErrorHandling(INSURANCE_DATA_QUERY, {
    variables: { documentId },
    skip: !documentId,
    notifyOnNetworkStatusChange: true,
    onCompleted: data => headerDataVar(data.insuranceData)
  });

  useQueryWithSimpleErrorHandling(HAS_CASCO_QUOTATIONS_HISTORY_QUERY, {
    variables: { documentId },
    onCompleted: data => {
      hasHistoryVar(data.hasCascoQuotationsHistory);
    },
    fetchPolicy: 'network-only'
  });

  useLocalSubscription(INSURANCE_QUOTATIONS_ARCHIVED, {
    variables: { documentId },
    skip: !documentId,
    onData: _ => {
      hasHistoryVar(true);
    }
  });

  useLocalSubscription(CONVENTIONS_QUOTATIONS_COMPUTING, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data }) => {
      const quotations = data?.conventionsQuotationsComputing?.insuranceQuotations || [];
      updateCachedQuotations(apolloClient, documentId, quotations);
      setInternalValues(quotations);
      quotationsInitializedVar(false);
    }
  });

  useLocalSubscription(INSURANCE_DOCUMENT_UPDATED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data }) => {
      headerDataVar(data?.insuranceDocumentUpdated?.insuranceData);
    }
  });

  useLocalSubscription(CASCO_QUOTATION_DATA_CHANGED, {
    variables: { documentId },
    skip: !documentId,
    onData: _ => {
      refetch();
    }
  });

  useLocalSubscription(CONVENTIONS_QUOTATIONS_RETRIEVED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data }) => {
      const quotations = data?.conventionsQuotationsRetrieved?.insuranceQuotations || [];
      updateCachedQuotations(apolloClient, documentId, quotations);
      setInternalValues(quotations);
      addToast(t('Offer.Edit.Casco.ConventionsComputed'), 'success', { toastId: 'conventionsComputedToast' });
    }
  });

  return <></>;
};

QuotationsSubscriptionsArea.propTypes = {
  contextRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  setInternalValues: PropTypes.func
};

export default QuotationsSubscriptionsArea;
